import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, Clock3 } from "lucide-react";

import { HubWorkspaceHeader, HubWorkspacePage } from "@/components/layout/HubWorkspace";
import { SalesOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";

import { AddWirelessPortalUserModal } from "./AddWirelessPortalUserModal";
import { ApprovePendingPortalUserModal } from "./ApprovePendingPortalUserModal";
import { ReviewWirelessPortalOrganizationModal } from "./ReviewWirelessPortalOrganizationModal";
import {
  addInsideSalesPortalUser,
  approveInsideSalesPendingPortalUser,
  denyInsideSalesPendingPortalUser,
  fetchInsideSalesPendingPortalUsers,
  fetchInsideSalesWirelessPortalAccounts,
  markInsideSalesWirelessPortalAccountDone,
  updateInsideSalesWirelessPortalOrganization,
  INSIDE_SALES_PENDING_PORTAL_USERS_QUERY_KEY,
  INSIDE_SALES_WIRELESS_PORTAL_ACCOUNTS_QUERY_KEY,
  type PendingPortalUserItem,
  type WirelessPortalAccountColumn,
  type WirelessPortalAccountItem,
} from "./insideSalesWirelessPortalAccountsApi";

const COLUMNS: Array<{ key: Exclude<WirelessPortalAccountColumn, "done" | "ready">; label: string }> = [
  { key: "needs_review", label: "Auto-created, needs review" },
  { key: "missing_admin", label: "Missing admin" },
  { key: "failed", label: "Failed auto-create" },
];

function ageLabel(updatedAt: string): string {
  const elapsed = Math.max(0, Date.now() - new Date(updatedAt).getTime());
  const hours = Math.floor(elapsed / 3_600_000);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function formatShipByDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match == null) return value;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function todayIsoDate(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function shipByIsDueOrPast(value: string): boolean {
  return value.slice(0, 10) <= todayIsoDate();
}

function sortedShipByDates(account: WirelessPortalAccountItem): string[] {
  const dates = [
    account.orderShipByDate,
    ...(account.manufacturingShipByDates ?? []),
    ...(account.wirelessShipByDates ?? []),
    account.earliestWirelessShipByDate,
  ].filter((date): date is string => date != null && date !== "");
  return [...new Set(dates.map((date) => date.slice(0, 10)))].sort();
}

function shipByChips(account: WirelessPortalAccountItem): Array<{
  key: string;
  label: string;
  date: string;
  hint: string;
}> {
  const orderDate = account.orderShipByDate?.slice(0, 10) ?? null;
  const manufacturingDates = [
    ...new Set((account.manufacturingShipByDates ?? []).map((date) => date.slice(0, 10))),
  ].sort();
  if (orderDate != null && manufacturingDates.length === 1 && manufacturingDates[0] === orderDate) {
    return [
      {
        key: `both-${orderDate}`,
        label: "Order & mfr ship by",
        date: orderDate,
        hint: "The sales order promised date and the manufacturing batch date are the same.",
      },
    ];
  }
  const chips: Array<{ key: string; label: string; date: string; hint: string }> = [];
  if (orderDate != null) {
    chips.push({
      key: `order-${orderDate}`,
      label: "Order ship by",
      date: orderDate,
      hint: "Promised ship-by date on the sales order.",
    });
  }
  for (const date of manufacturingDates) {
    chips.push({
      key: `mfr-${date}`,
      label: "Mfr ship by",
      date,
      hint: "Manufacturing batch ship-by date for wireless lines on this order.",
    });
  }
  return chips.sort((left, right) => left.date.localeCompare(right.date) || left.label.localeCompare(right.label));
}

function adminBadge(account: WirelessPortalAccountItem): { label: string; className: string } | null {
  if (account.portalAdminCount == null) return null;
  if (account.portalAdminCount <= 0) {
    return { label: "No admin", className: "bg-amber-50 text-amber-900" };
  }
  const label = account.portalAdminCount === 1 ? "1 admin" : `${account.portalAdminCount} admins`;
  return { label, className: "bg-emerald-50 text-emerald-800" };
}

function AccountCard({
  account,
  onMarkDone,
  isMarkingDone,
  onOpen,
}: {
  account: WirelessPortalAccountItem;
  onMarkDone?: (account: WirelessPortalAccountItem) => void;
  isMarkingDone: boolean;
  onOpen: (account: WirelessPortalAccountItem) => void;
}) {
  const badge = adminBadge(account);
  const salesOrderId = Number(account.salesOrderId);
  const canMarkDone = onMarkDone != null && (account.column === "needs_review" || account.column === "missing_admin");

  return (
    <article
      className="cursor-pointer rounded-lg border border-slate-200 bg-white p-3 text-left shadow-sm hover:border-slate-300"
      onClick={() => onOpen(account)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {Number.isFinite(salesOrderId) && salesOrderId > 0 ? (
            <span onClick={(event) => event.stopPropagation()}>
              <SalesOrderDetailLink
                salesOrderId={salesOrderId}
                salesOrderNumber={account.salesOrderNumber}
                className="text-sm font-semibold"
              />
            </span>
          ) : (
            <span className="text-sm font-semibold text-slate-900">{account.salesOrderNumber}</span>
          )}
          <div className="mt-1 truncate text-sm text-slate-800">
            {account.endUserName || `FS ${account.freshsalesAccountId}`}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Clock3 className="h-3.5 w-3.5" aria-hidden />
            {ageLabel(account.updatedAt)}
          </span>
          {canMarkDone ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
              disabled={isMarkingDone}
              title="Mark done"
              aria-label="Mark done"
              onClick={(event) => {
                event.stopPropagation();
                onMarkDone(account);
              }}
            >
              <CircleCheck className="h-4 w-4" aria-hidden />
            </Button>
          ) : account.column === "done" ? (
            <CircleCheck className="h-4 w-4 text-emerald-600" aria-label="Done" />
          ) : null}
        </div>
      </div>
      <div className="mt-2 space-y-1 text-xs text-slate-600">
        {account.portalOrganizationName ? (
          <div>Portal org: {account.portalOrganizationName}</div>
        ) : null}
        {shipByChips(account).map((chip) => {
          const overdue = shipByIsDueOrPast(chip.date);
          return (
            <div key={chip.key}>
              <span
                title={chip.hint}
                className={
                  overdue
                    ? "inline-flex rounded bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-800"
                    : "inline-flex rounded bg-sky-50 px-1.5 py-0.5 text-xs font-medium text-sky-900"
                }
              >
                {chip.label} {formatShipByDate(chip.date)}
              </span>
            </div>
          );
        })}
        {badge ? (
          <div>
            <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          </div>
        ) : null}
        <div onClick={(event) => event.stopPropagation()}>
          Unshipped sales orders:{" "}
          {account.unshippedSalesOrders.length === 0
            ? "none"
            : account.unshippedSalesOrders.map((order, index) => (
                <span key={order.salesOrderId}>
                  {index > 0 ? ", " : null}
                  <SalesOrderDetailLink
                    salesOrderId={Number(order.salesOrderId)}
                    salesOrderNumber={order.salesOrderNumber}
                  />
                </span>
              ))}
        </div>
        {account.errorMessage ? <div className="text-rose-700">{account.errorMessage}</div> : null}
      </div>
    </article>
  );
}

function PendingUserCard({
  user,
  onOpen,
}: {
  user: PendingPortalUserItem;
  onOpen: () => void;
}) {
  const adminName = `${user.adminFirstName} ${user.adminLastName}`.trim();
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full rounded-lg border border-slate-200 bg-white p-3 text-left shadow-sm hover:border-slate-300"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">{adminName || user.adminEmail}</div>
          <div className="mt-1 truncate text-sm text-slate-800">{user.organizationName || "No organization name"}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {user.status !== "Pending" ? (
            <span className="inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
              {user.status}
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Clock3 className="h-3.5 w-3.5" aria-hidden />
            {ageLabel(user.createdAt)}
          </span>
        </div>
      </div>
    </button>
  );
}

function BoardColumn({
  title,
  count,
  headerExtra,
  children,
}: {
  title: string;
  count: number;
  headerExtra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex h-full min-h-0 min-w-0 flex-col gap-3">
      <div className="flex shrink-0 items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        <div className="flex items-center gap-2">
          {headerExtra}
          <span className="text-xs text-slate-500">{count}</span>
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
        {children}
      </div>
    </section>
  );
}

export function InsideSalesWirelessPortalAccountsPage() {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [includeDone, setIncludeDone] = useState(false);
  const [requestStatus, setRequestStatus] = useState("Pending");
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [reviewOrganizationAccount, setReviewOrganizationAccount] = useState<WirelessPortalAccountItem | null>(null);
  const [approvePendingUser, setApprovePendingUser] = useState<PendingPortalUserItem | null>(null);

  const accountsQuery = useQuery({
    queryKey: [...INSIDE_SALES_WIRELESS_PORTAL_ACCOUNTS_QUERY_KEY, includeDone],
    queryFn: () => fetchInsideSalesWirelessPortalAccounts(includeDone),
  });
  const pendingUsersQuery = useQuery({
    queryKey: [...INSIDE_SALES_PENDING_PORTAL_USERS_QUERY_KEY, requestStatus],
    queryFn: () => fetchInsideSalesPendingPortalUsers(requestStatus),
  });

  const addUserMutation = useMutation({
    mutationFn: addInsideSalesPortalUser,
    onSuccess: async (result) => {
      setAddUserOpen(false);
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_WIRELESS_PORTAL_ACCOUNTS_QUERY_KEY });
      showToast({
        message: `${result.email} created in the portal.`,
        tone: "success",
      });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Could not add the portal user.";
      showToast({ message, tone: "error" });
    },
  });

  const updateOrganizationMutation = useMutation({
    mutationFn: (input: { accountId: string; name: string; domain: string }) =>
      updateInsideSalesWirelessPortalOrganization(input.accountId, {
        name: input.name,
        domain: input.domain,
      }),
    onSuccess: async (result) => {
      setReviewOrganizationAccount(null);
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_WIRELESS_PORTAL_ACCOUNTS_QUERY_KEY });
      showToast({ message: `Portal organization name saved as ${result.name}.`, tone: "success" });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Could not update the portal organization.";
      showToast({ message, tone: "error" });
    },
  });

  const approvePendingUserMutation = useMutation({
    mutationFn: (input: {
      userId: string;
      organizationId: number;
      organizationName: string;
      timeZone: string;
    }) =>
      approveInsideSalesPendingPortalUser(input.userId, {
        organizationId: input.organizationId,
        organizationName: input.organizationName,
        timeZone: input.timeZone,
      }),
    onSuccess: async (result) => {
      setApprovePendingUser(null);
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_PENDING_PORTAL_USERS_QUERY_KEY });
      const created = result.createdEmails?.join(", ") || result.user.adminEmail;
      showToast({
        message: `${created} created in the portal.`,
        tone: "success",
      });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Could not approve the pending portal user.";
      showToast({ message, tone: "error" });
    },
  });

  const denyPendingUserMutation = useMutation({
    mutationFn: (userId: string) => denyInsideSalesPendingPortalUser(userId),
    onSuccess: async (result) => {
      setApprovePendingUser(null);
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_PENDING_PORTAL_USERS_QUERY_KEY });
      showToast({ message: `${result.user.adminEmail} marked denied.`, tone: "info" });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Could not deny the pending portal user.";
      showToast({ message, tone: "error" });
    },
  });

  const markDoneMutation = useMutation({
    mutationFn: (accountId: string) => markInsideSalesWirelessPortalAccountDone(accountId),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_WIRELESS_PORTAL_ACCOUNTS_QUERY_KEY });
      if (result.account.column === "missing_admin") {
        showToast({
          message: "Moved to Missing admin. The portal organization still needs an admin user.",
          tone: "info",
        });
        return;
      }
      if (result.account.column === "done") {
        showToast({ message: "Wireless portal account marked done.", tone: "success" });
      }
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Could not update wireless portal account.";
      showToast({ message, tone: "error" });
    },
  });

  const grouped = useMemo(() => {
    const accounts = accountsQuery.data?.accounts ?? [];
    const buckets: Record<WirelessPortalAccountColumn, WirelessPortalAccountItem[]> = {
      needs_review: [],
      missing_admin: [],
      ready: [],
      failed: [],
      done: [],
    };
    for (const account of accounts) {
      buckets[account.column].push(account);
    }
    for (const column of Object.keys(buckets) as WirelessPortalAccountColumn[]) {
      buckets[column].sort((left, right) => {
        const leftDate = sortedShipByDates(left)[0] ?? "9999-12-31";
        const rightDate = sortedShipByDates(right)[0] ?? "9999-12-31";
        return leftDate.localeCompare(rightDate);
      });
    }
    return buckets;
  }, [accountsQuery.data?.accounts]);

  return (
    <HubWorkspacePage>
      <HubWorkspaceHeader className="flex flex-col gap-3">
      <PageTitleWithHelp hoverHint="Wireless portal tasks open when approved sales orders include wireless units. Order ship by is the promised date on the sales order. Mfr ship by is the manufacturing batch date for wireless lines. Unshipped orders for the same end user stay on the card. Portal requests come from website contact submissions; use the column dropdown to show Pending, Approved, or Denied. Click a request to review people. Click an account card for ticket details. The sales order number opens manufacturing management.">
        Wireless portal
      </PageTitleWithHelp>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" size="sm" onClick={() => setAddUserOpen(true)}>
          Add user
        </Button>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={includeDone}
            onChange={(event) => setIncludeDone(event.target.checked)}
          />
          Show done
        </label>
      </div>
      </HubWorkspaceHeader>

      {accountsQuery.isLoading || pendingUsersQuery.isLoading ? (
        <Card><CardBody className="py-6 text-sm text-slate-600">Loading wireless portal...</CardBody></Card>
      ) : null}
      {accountsQuery.isError ? (
        <Card><CardBody className="py-6 text-sm text-rose-700">{accountsQuery.error.message}</CardBody></Card>
      ) : null}
      {pendingUsersQuery.isError ? (
        <Card><CardBody className="py-6 text-sm text-rose-700">{pendingUsersQuery.error.message}</CardBody></Card>
      ) : null}

      <div className="grid min-h-0 flex-1 gap-4 overflow-hidden xl:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)]">
        <div className="grid min-h-0 min-w-0 gap-4 overflow-hidden md:grid-cols-3">
          {COLUMNS.map((column) => (
            <BoardColumn key={column.key} title={column.label} count={grouped[column.key].length}>
              {grouped[column.key].length === 0 ? (
                <Card>
                  <CardBody className="py-4 text-sm text-slate-500">No rows in this column.</CardBody>
                </Card>
              ) : (
                grouped[column.key].map((account) => (
                  <AccountCard
                    key={account.id}
                    account={account}
                    onMarkDone={(row) => markDoneMutation.mutate(row.id)}
                    isMarkingDone={markDoneMutation.isPending}
                    onOpen={setReviewOrganizationAccount}
                  />
                ))
              )}
            </BoardColumn>
          ))}
        </div>
        <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-t border-slate-200 pt-4 xl:border-l xl:border-t-0 xl:pl-4 xl:pt-0">
          <BoardColumn
            title="Portal requests"
            count={(pendingUsersQuery.data?.users ?? []).length}
            headerExtra={(
              <select
                className="h-7 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-800"
                value={requestStatus}
                aria-label="Portal request status"
                onChange={(event) => setRequestStatus(event.target.value)}
              >
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Denied">Denied</option>
              </select>
            )}
          >
            {(pendingUsersQuery.data?.users ?? []).length === 0 ? (
              <Card>
                <CardBody className="py-4 text-sm text-slate-500">
                  No {requestStatus.toLowerCase()} requests.
                </CardBody>
              </Card>
            ) : (
              (pendingUsersQuery.data?.users ?? []).map((user) => (
                <PendingUserCard
                  key={user.id}
                  user={user}
                  onOpen={() => setApprovePendingUser(user)}
                />
              ))
            )}
          </BoardColumn>
        </div>
      </div>

      {includeDone && grouped.done.length > 0 ? (
        <section className="min-h-0 shrink-0 space-y-3 overflow-y-auto">
          <h2 className="text-sm font-semibold text-slate-900">Done</h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {grouped.done.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                isMarkingDone={false}
                onOpen={setReviewOrganizationAccount}
              />
            ))}
          </div>
        </section>
      ) : null}

      <ApprovePendingPortalUserModal
        user={approvePendingUser}
        pending={approvePendingUserMutation.isPending}
        denyPending={denyPendingUserMutation.isPending}
        onClose={() => {
          if (!approvePendingUserMutation.isPending && !denyPendingUserMutation.isPending) {
            setApprovePendingUser(null);
          }
        }}
        onApprove={(input) => {
          if (approvePendingUser == null) return;
          approvePendingUserMutation.mutate({ userId: approvePendingUser.id, ...input });
        }}
        onDeny={() => {
          if (approvePendingUser == null) return;
          denyPendingUserMutation.mutate(approvePendingUser.id);
        }}
      />
      <AddWirelessPortalUserModal
        open={addUserOpen}
        pending={addUserMutation.isPending}
        onClose={() => {
          if (!addUserMutation.isPending) setAddUserOpen(false);
        }}
        onCreate={(input) => addUserMutation.mutate(input)}
      />
      <ReviewWirelessPortalOrganizationModal
        account={reviewOrganizationAccount}
        pending={updateOrganizationMutation.isPending}
        onClose={() => {
          if (!updateOrganizationMutation.isPending) setReviewOrganizationAccount(null);
        }}
        onSave={(input) => {
          if (reviewOrganizationAccount == null) return;
          updateOrganizationMutation.mutate({ accountId: reviewOrganizationAccount.id, ...input });
        }}
      />
    </HubWorkspacePage>
  );
}
